import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router';
import './FankiLanding.css';

export default function FankiLandingPage() {
    const navRef = useRef(null);
    const navigate = useNavigate();
    const [previewTheme, setPreviewTheme] = useState('dark');
    const sliderRef = useRef(null);

    // Timer: fixed deadline — March 31, 2026 23:59 Moscow time
    const getDeadline = useCallback(() => {
        return new Date('2026-03-31T23:59:59+03:00').getTime();
    }, []);

    const [timeLeft, setTimeLeft] = useState(() => {
        const diff = getDeadline() - Date.now();
        return diff > 0 ? diff : 0;
    });

    useEffect(() => {
        const timer = setInterval(() => {
            const diff = getDeadline() - Date.now();
            setTimeLeft(diff > 0 ? diff : 0);
        }, 1000);
        return () => clearInterval(timer);
    }, [getDeadline]);

    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

    useEffect(() => {
        // Scroll-triggered fade-in
        const io = new IntersectionObserver(entries => {
            entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('fk-v'); });
        }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

        document.querySelectorAll('.fk-fi').forEach(el => io.observe(el));

        // Nav background on scroll
        const onScroll = () => {
            if (navRef.current) {
                navRef.current.classList.toggle('scrolled', window.scrollY > 60);
            }
        };
        window.addEventListener('scroll', onScroll, { passive: true });

        // Scroll to top on mount
        window.scrollTo(0, 0);

        // GetCourse widget scripts
        const gcScript = document.createElement('script');
        gcScript.id = '4e10cef5c7523398950f3041175001859f144fa0';
        gcScript.src = 'https://apexmeded.ru/pl/lite/widget/script?id=1579798';
        gcScript.async = true;
        document.body.appendChild(gcScript);

        const gcScriptEN = document.createElement('script');
        gcScriptEN.id = 'a0b0cfd4119344bdafb30ff7fb602dbf22360c00';
        gcScriptEN.src = 'https://apexmeded.ru/pl/lite/widget/script?id=1579910';
        gcScriptEN.async = true;
        document.body.appendChild(gcScriptEN);

        const gcScriptRU = document.createElement('script');
        gcScriptRU.id = '113e4b1066599581661d81e5d19641865feaeabc';
        gcScriptRU.src = 'https://apexmeded.ru/pl/lite/widget/script?id=1579911';
        gcScriptRU.async = true;
        document.body.appendChild(gcScriptRU);

        return () => {
            io.disconnect();
            window.removeEventListener('scroll', onScroll);
            if (document.body.contains(gcScript)) document.body.removeChild(gcScript);
            if (document.body.contains(gcScriptEN)) document.body.removeChild(gcScriptEN);
            if (document.body.contains(gcScriptRU)) document.body.removeChild(gcScriptRU);
        };
    }, []);

    const scrollTo = (e, id) => {
        e.preventDefault();
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div className="fanki-page">

            {/* NAV */}
            <nav className="fk-nav" ref={navRef}>
                <div className="fk-nav-inner">
                    <RouterLink to="/" className="fk-logo">FANKI</RouterLink>
                    <a href="#fk-pricing" className="fk-nav-btn" onClick={(e) => { e.preventDefault(); document.getElementById('fk-pricing')?.scrollIntoView({ behavior: 'smooth' }); }}>Получить</a>
                </div>
            </nav>

            <main>

                {/* 1. HERO */}
                <header className="fk-s fk-deep hero" id="fk-hero">
                    <div className="hero-horizon"></div>
                    <div className="hero-grid"></div>
                    <div className="hero-glow"></div>
                    <div className="hero-glow-2"></div>
                    <div className="fk-w hero-w">
                        <p className="overline fk-fi">Система интервального повторения</p>
                        <h1 className="fk-fi">FANKI - 12&nbsp;000&nbsp;Anki-карточек для USMLE&nbsp;Step&nbsp;1</h1>
                        <p className="hero-sub fk-fi">12&nbsp;000 Anki-карточек по всему First Aid. Две коллекции: на русском и английском. От врача-резидента, Internal Medicine Match.</p>
                        <a href="#fk-pricing" onClick={(e) => { e.preventDefault(); document.getElementById('fk-pricing')?.scrollIntoView({ behavior: 'smooth' }); }} className="fk-btn fk-fi">
                            Получить FANKI - от&nbsp;5&nbsp;900&nbsp;₽
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                        </a>
                        <p className="hero-anchor fk-fi">Меньше 1₽ за карточку. Дешевле одного занятия с репетитором.</p>
                        <p className="hero-bonus fk-fi">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12v10H4V12"/><path d="M2 7h20v5H2z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>
                            В подарок - PDF-гайд по Anki: настройка, стратегия повторения, лайфхаки
                        </p>
                        <p className="hero-note fk-fi">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="m7 10 5 5 5-5"/><path d="M12 15V3"/></svg>
                            Попробуй бесплатно - <a href="https://t.me/ashurovmed/3222" target="_blank" rel="noopener noreferrer" className="hero-demo-link" style={{color: 'var(--accent)', fontWeight: 700}}>скачай тестовый пак по иммунологии (440 карточек)</a>
                        </p>
                    </div>
                </header>

                {/* STATS STRIP */}
                <div className="stats-strip">
                    <div className="fk-w">
                        <div className="stats fk-fi">
                            <div className="stat">
                                <span className="stat-num">&lt; 1 ₽</span>
                                <span className="stat-txt">за карточку</span>
                            </div>
                            <div className="stat">
                                <span className="stat-num">12 000</span>
                                <span className="stat-txt">карточек</span>
                            </div>
                            <div className="stat">
                                <span className="stat-num">100%</span>
                                <span className="stat-txt">First Aid</span>
                            </div>
                            <div className="stat">
                                <span className="stat-num">500+</span>
                                <span className="stat-txt">учеников</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2. PROBLEM */}
                <section className="fk-s fk-beige" id="fk-problem">
                    <div className="fk-w fk-w--narrow">
                        <h2 className="fk-fi">Проблема</h2>
                        <div className="prose fk-fi">
                            <p>Ты готовишься к Step&nbsp;1 и знаешь, как это выглядит изнутри: First Aid прочитан, но через неделю половина забыта. Ты читаешь снова. Забываешь снова. Цикл повторяется, а экзамен приближается.</p>
                            <p>Проблема не в тебе. Проблема в том, что чтение без системы повторения не работает. <strong>Мозг не хранит то, что не повторяет.</strong></p>
                        </div>
                    </div>
                </section>

                {/* FOR WHOM */}
                <section className="fk-s fk-beige" id="fk-audience">
                    <div className="fk-w fk-w--narrow">
                        <h2 className="fk-fi">Для кого FANKI</h2>
                        <div className="prose fk-fi fk-audience-intro">
                            <p>FANKI для тех, кто нацелен улучшить свои знания, <strong>сдать USMLE Step&nbsp;1 и создать фундамент для Step&nbsp;2&nbsp;CK.</strong></p>
                        </div>
                        <div className="fk-audience-list">
                            <div className="fk-audience-item fk-fi">
                                <div className="fk-audience-icon">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c0 1.1 2.7 2 6 2s6-.9 6-2v-5"/></svg>
                                </div>
                                <div>
                                    <h3>IMG, готовящийся к Step 1</h3>
                                    <p>Тебе нужна система, а не просто учебник. FANKI превращает First Aid в ежедневную привычку.</p>
                                </div>
                            </div>
                            <div className="fk-audience-item fk-fi">
                                <div className="fk-audience-icon">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
                                </div>
                                <div>
                                    <h3>Уже прочитал First Aid, но забываешь</h3>
                                    <p>Чтение без повторения - потерянное время. Anki решает именно эту проблему.</p>
                                </div>
                            </div>
                            <div className="fk-audience-item fk-fi">
                                <div className="fk-audience-icon">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
                                </div>
                                <div>
                                    <h3>Пробовал AnKing/Zanki, но утонул в 30 000 карточках</h3>
                                    <p>FANKI - это 12 000 отфильтрованных карточек. Только HIGH YIELD, ничего лишнего.</p>
                                </div>
                            </div>
                            <div className="fk-audience-item fk-fi">
                                <div className="fk-audience-icon">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="m5 8 6 6"/><path d="m4 14 6-6 2-3"/><path d="M2 5h12"/><path d="M7 2h1"/><path d="m22 22-5-10-5 10"/><path d="M14 18h6"/></svg>
                                </div>
                                <div>
                                    <h3>Хочешь готовиться на русском, не теряя английскую терминологию</h3>
                                    <p>Две коллекции - русская и английская. Учишь на родном языке, сдаёшь на английском.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 3. SOLUTION */}
                <section className="fk-s fk-deep" id="fk-solution">
                    <div className="fk-w fk-w--narrow">
                        <h2 className="fk-fi">Решение</h2>
                        <div className="prose fk-fi">
                            <p><strong>FANKI - это не просто колода карточек.</strong> Каждая карточка прошла через фильтр: если это не спрашивают на экзамене - её здесь нет.</p>
                            <p>Именно так я готовился сам. Именно так я сдал Step&nbsp;1 на&nbsp;239 и Step&nbsp;2&nbsp;CK на&nbsp;260.</p>
                        </div>
                        <div className="fk-pipeline fk-fi">
                            <div className="fk-pipe-step">
                                <div className="fk-pipe-icon">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
                                </div>
                                <span className="fk-pipe-num">Шаг 1</span>
                                <span className="fk-pipe-label">Читаешь</span>
                            </div>
                            <div className="fk-pipe-arrow">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                            </div>
                            <div className="fk-pipe-step">
                                <div className="fk-pipe-icon">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="18" height="14" rx="2"/><path d="M8 21h8"/><path d="M12 17v4"/><path d="M7 8h6"/><path d="M7 11h4"/></svg>
                                </div>
                                <span className="fk-pipe-num">Шаг 2</span>
                                <span className="fk-pipe-label">Решаешь Anki</span>
                            </div>
                            <div className="fk-pipe-arrow">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                            </div>
                            <div className="fk-pipe-step">
                                <div className="fk-pipe-icon">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/><path d="m9 12 2 2 4-4"/></svg>
                                </div>
                                <span className="fk-pipe-num">Шаг 3</span>
                                <span className="fk-pipe-label">Закрепляешь задачами</span>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 4. WHAT'S INSIDE */}
                <section className="fk-s fk-cream" id="fk-inside">
                    <div className="fk-w">
                        <h2 className="fk-fi">Что внутри</h2>
                        <div className="grid-4">
                            <div className="fk-card fk-fi">
                                <div className="fk-card-icon">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="m12 2 10 6.5-10 6.5L2 8.5Z"/><path d="m2 12 10 6.5L22 12"/><path d="m2 17 10 6.5 10-6.5"/></svg>
                                </div>
                                <h3>12 000 карточек</h3>
                                <p>Весь First Aid от биохимии до фармакологии без пропусков</p>
                            </div>
                            <div className="fk-card fk-fi">
                                <div className="fk-card-icon">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>
                                </div>
                                <h3>Basic и Cloze форматы</h3>
                                <p>Для глубокого, а не поверхностного запоминания</p>
                            </div>
                            <div className="fk-card fk-fi">
                                <div className="fk-card-icon">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z"/></svg>
                                </div>
                                <h3>Авторская структура</h3>
                                <p>Не перевод AnKing, не адаптация Zanki. Создано с нуля, вопросы четкие, важные, краткие</p>
                            </div>
                            <div className="fk-card fk-fi">
                                <div className="fk-card-icon">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="m7 10 5 5 5-5"/><path d="M12 15V3"/></svg>
                                </div>
                                <h3>Готово к использованию</h3>
                                <p>Скачал, открыл в Anki, начал повторять. Никакой настройки, никаких пробелов</p>
                            </div>
                            <div className="fk-card fk-card--bonus fk-fi">
                                <div className="fk-card-icon fk-card-icon--bonus">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12v10H4V12"/><path d="M2 7h20v5H2z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>
                                </div>
                                <span className="fk-card-badge">Бонус</span>
                                <h3>PDF-гайд по Anki</h3>
                                <p>Как настроить Anki, выстроить расписание повторений и не бросить через неделю. Идёт в подарок к колоде</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CARD PREVIEW */}
                <section className="fk-s fk-deep" id="fk-preview">
                    <div className="fk-w">
                        <h2 className="fk-fi" style={{textAlign: 'center'}}>Как выглядят карточки</h2>
                        <p className="fk-preview-sub fk-fi">Авторский дизайн APEX FANKI. Адаптируется под настройки Anki.</p>

                        <div className="fk-preview-tabs fk-fi">
                            <button className={`fk-preview-tab ${previewTheme === 'dark' ? 'active' : ''}`} onClick={() => setPreviewTheme('dark')}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
                                Dark
                            </button>
                            <button className={`fk-preview-tab ${previewTheme === 'light' ? 'active' : ''}`} onClick={() => setPreviewTheme('light')}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>
                                Light
                            </button>
                        </div>

                        <div className="fk-slider-wrap fk-fi">
                            <button className="fk-slider-arrow fk-slider-arrow--left" onClick={() => { if (sliderRef.current) sliderRef.current.scrollBy({ left: -340, behavior: 'smooth' }); }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
                            </button>
                            <div className="fk-slider" ref={sliderRef}>
                                {previewTheme === 'dark' ? (<>
                                    <div className="fk-slide"><img src="/fanki-assets/dark-q1.png" alt="Dark - вопрос" /><span className="fk-slide-cap">Вопрос</span></div>
                                    <div className="fk-slide"><img src="/fanki-assets/dark-a1.png" alt="Dark - ответ" /><span className="fk-slide-cap">Ответ + Extra</span></div>
                                    <div className="fk-slide"><img src="/fanki-assets/dark-q2.png" alt="Dark - вопрос 2" /><span className="fk-slide-cap">Вопрос</span></div>
                                    <div className="fk-slide"><img src="/fanki-assets/dark-a2.png" alt="Dark - ответ 2" /><span className="fk-slide-cap">Ответ + Extra</span></div>
                                </>) : (<>
                                    <div className="fk-slide"><img src="/fanki-assets/light-q1.png" alt="Light - вопрос" /><span className="fk-slide-cap">Вопрос</span></div>
                                    <div className="fk-slide"><img src="/fanki-assets/light-a1.png" alt="Light - ответ" /><span className="fk-slide-cap">Ответ + Extra</span></div>
                                    <div className="fk-slide"><img src="/fanki-assets/light-q2.png" alt="Light - вопрос 2" /><span className="fk-slide-cap">Вопрос</span></div>
                                    <div className="fk-slide"><img src="/fanki-assets/light-a2.png" alt="Light - ответ 2" /><span className="fk-slide-cap">Ответ + Extra</span></div>
                                </>)}
                            </div>
                            <button className="fk-slider-arrow fk-slider-arrow--right" onClick={() => { if (sliderRef.current) sliderRef.current.scrollBy({ left: 340, behavior: 'smooth' }); }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                            </button>
                        </div>

                        <div className="fk-preview-deck fk-fi">
                            <span className="fk-preview-label">Структура колоды</span>
                            <img src="/fanki-assets/deck-overview.png" alt="FANKI deck overview" loading="lazy" />
                            <div className="fk-preview-deck-grid">
                                <div>
                                    <span className="fk-preview-label">English коллекция</span>
                                    <img src="/fanki-assets/deck-en.png" alt="FANKI EN deck structure" loading="lazy" />
                                </div>
                                <div>
                                    <span className="fk-preview-label">Русская коллекция</span>
                                    <img src="/fanki-assets/deck-ru.png" alt="FANKI RU deck structure" loading="lazy" />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 5. WHO CREATED */}
                <section className="fk-s fk-beige" id="fk-author">
                    <div className="fk-w fk-w--narrow">
                        <h2 className="fk-fi">Об авторе</h2>
                        <div className="author fk-fi">
                            <div className="author-mono">МА</div>
                            <div>
                                <h3>Мухаммад Ашуров</h3>
                                <p className="author-role">Врач-резидент, Internal Medicine Match</p>
                            </div>
                        </div>
                        <div className="scores fk-fi">
                            <div className="score">
                                <span className="score-num">239</span>
                                <span className="score-label">Step 1</span>
                            </div>
                            <div className="score">
                                <span className="score-num">260</span>
                                <span className="score-label">Step 2 CK</span>
                            </div>
                            <div className="score">
                                <span className="score-num">2026</span>
                                <span className="score-label">Match</span>
                            </div>
                        </div>
                        <div className="prose fk-fi">
                            <p>5 лет преподавания, 500+ обученных учеников и десятки сдавших USMLE позволили мне создать самую HIGH YIELD колоду для Step&nbsp;1.</p>
                        </div>
                    </div>
                </section>

                {/* 6. COMPARISON TABLE */}
                <section className="fk-s fk-cream" id="fk-compare">
                    <div className="fk-w">
                        <h2 className="fk-fi">Сравнение</h2>
                        <div className="fk-table-wrap fk-fi">
                            <table className="fk-table">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>Сделать самому</th>
                                        <th>Другие колоды</th>
                                        <th className="fk-table-hl">FANKI</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="fk-table-author-row">
                                        <td className="fk-table-label">Автор</td>
                                        <td>Ты</td>
                                        <td>Готовится к Step 1</td>
                                        <td className="fk-table-hl fk-table-author-fanki">Сдал Step 1 (239) + Match</td>
                                    </tr>
                                    <tr>
                                        <td className="fk-table-label">Количество карточек</td>
                                        <td>Зависит от тебя</td>
                                        <td>13 000 - 30 000</td>
                                        <td className="fk-table-hl">12 000 HIGH YIELD</td>
                                    </tr>
                                    <tr>
                                        <td className="fk-table-label">Время на подготовку</td>
                                        <td>1 000+ часов</td>
                                        <td>0 часов</td>
                                        <td className="fk-table-hl">0 часов</td>
                                    </tr>
                                    <tr>
                                        <td className="fk-table-label">Фильтрация low-yield</td>
                                        <td>Нет гарантии</td>
                                        <td><span className="fk-table-icon fk-table-icon--no"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg></span></td>
                                        <td className="fk-table-hl"><span className="fk-table-icon fk-table-icon--yes"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6 9 17l-5-5"/></svg></span></td>
                                    </tr>
                                    <tr>
                                        <td className="fk-table-label">Русский язык</td>
                                        <td>Если сам переведёшь</td>
                                        <td><span className="fk-table-icon fk-table-icon--no"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg></span></td>
                                        <td className="fk-table-hl"><span className="fk-table-icon fk-table-icon--yes"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6 9 17l-5-5"/></svg></span></td>
                                    </tr>
                                    <tr>
                                        <td className="fk-table-label">Английский язык</td>
                                        <td>Если сам напишешь</td>
                                        <td><span className="fk-table-icon fk-table-icon--yes"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6 9 17l-5-5"/></svg></span></td>
                                        <td className="fk-table-hl"><span className="fk-table-icon fk-table-icon--yes"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6 9 17l-5-5"/></svg></span></td>
                                    </tr>
                                    <tr>
                                        <td className="fk-table-label">Проверено врачом</td>
                                        <td><span className="fk-table-icon fk-table-icon--no"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg></span></td>
                                        <td><span className="fk-table-icon fk-table-icon--no"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg></span></td>
                                        <td className="fk-table-hl"><span className="fk-table-icon fk-table-icon--yes"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6 9 17l-5-5"/></svg></span></td>
                                    </tr>
                                    <tr>
                                        <td className="fk-table-label">Ошибки и устаревший материал</td>
                                        <td>Возможны</td>
                                        <td><span className="fk-table-icon fk-table-icon--no" style={{color: '#dc2626'}}>Много</span></td>
                                        <td className="fk-table-hl"><span className="fk-table-icon fk-table-icon--yes"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6 9 17l-5-5"/></svg></span></td>
                                    </tr>
                                    <tr>
                                        <td className="fk-table-label">Структура по First Aid</td>
                                        <td>Нужно организовать</td>
                                        <td>Частично</td>
                                        <td className="fk-table-hl" style={{color: '#16a34a'}}>100%</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>


                {/* HOW TO START */}
                <section className="fk-s fk-cream" id="fk-start">
                    <div className="fk-w fk-w--narrow">
                        <h2 className="fk-fi">Как начать</h2>
                        <div className="fk-pipeline fk-fi">
                            <div className="fk-pipe-step">
                                <div className="fk-pipe-icon fk-pipe-icon--cream">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M12 17v4"/><path d="M8 21h8"/></svg>
                                </div>
                                <span className="fk-pipe-num fk-pipe-num--cream">Шаг 1</span>
                                <span className="fk-pipe-label fk-pipe-label--cream">Купи FANKI</span>
                            </div>
                            <div className="fk-pipe-arrow fk-pipe-arrow--cream">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                            </div>
                            <div className="fk-pipe-step">
                                <div className="fk-pipe-icon fk-pipe-icon--cream">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="m7 10 5 5 5-5"/><path d="M12 15V3"/></svg>
                                </div>
                                <span className="fk-pipe-num fk-pipe-num--cream">Шаг 2</span>
                                <span className="fk-pipe-label fk-pipe-label--cream">Скачай и открой в Anki</span>
                                <span className="fk-pipe-hint">Бесплатная программа</span>
                            </div>
                            <div className="fk-pipe-arrow fk-pipe-arrow--cream">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                            </div>
                            <div className="fk-pipe-step">
                                <div className="fk-pipe-icon fk-pipe-icon--cream">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/><path d="m9 12 2 2 4-4"/></svg>
                                </div>
                                <span className="fk-pipe-num fk-pipe-num--cream">Шаг 3</span>
                                <span className="fk-pipe-label fk-pipe-label--cream">Повторяй 20 мин в день</span>
                            </div>
                        </div>
                    </div>
                </section>

                {/* PRICING */}
                <section className="fk-s fk-deep" id="fk-pricing">
                    <div className="fk-w">
                        <h2 className="fk-fi" style={{textAlign: 'center'}}>Выбери свой вариант</h2>
                        <div className="fk-pricing-grid fk-fi">
                            <div className="fk-price-card">
                                <div className="fk-price-lang">RU</div>
                                <h3 className="fk-price-title">FANKI RU</h3>
                                <p className="fk-price-count">12 500+ карточек</p>
                                <p className="fk-price-desc">Вся колода на русском языке. Идеально для подготовки на родном языке.</p>
                                <div className="fk-price-amount">5 900 <span>₽</span></div>
                                <a href="#" onClick={(e) => { e.preventDefault(); window.open('https://apexmeded.ru/pl/lite/widget/widget?id=1579911', '_blank'); }} className="fk-price-btn fk-price-btn--dark">Получить FANKI RU</a>
                            </div>

                            <div className="fk-price-card fk-price-card--best">
                                <div className="fk-price-badge-top">Выгодно</div>
                                <div className="fk-price-lang">RU + EN</div>
                                <h3 className="fk-price-title">FANKI BUNDLE</h3>
                                <p className="fk-price-count">25 000+ карточек</p>
                                <p className="fk-price-desc">Обе коллекции: русская и английская. Разовая покупка на всю жизнь.</p>
                                <div className="fk-price-old">12 900 ₽</div>
                                <div className="fk-price-amount">7 900 <span>₽</span></div>
                                <p className="fk-price-save">Экономия 5 000 ₽ (-39%)</p>
                                <a href="#" onClick={(e) => { e.preventDefault(); const w = document.querySelector('.gc-widget-btn, [class*="gc-"], [id*="gc-"]'); if (w) w.click(); else window.open('https://apexmeded.ru/pl/lite/widget/widget?id=1579798', '_blank'); }} className="fk-price-btn fk-price-btn--best">Получить FANKI</a>
                            </div>

                            <div className="fk-price-card">
                                <div className="fk-price-lang">EN</div>
                                <h3 className="fk-price-title">FANKI EN</h3>
                                <p className="fk-price-count">12 500+ карточек</p>
                                <p className="fk-price-desc">Вся колода на английском. Для тех, кто готовится сразу на языке экзамена.</p>
                                <div className="fk-price-amount">5 900 <span>₽</span></div>
                                <a href="#" onClick={(e) => { e.preventDefault(); window.open('https://apexmeded.ru/pl/lite/widget/widget?id=1579910', '_blank'); }} className="fk-price-btn fk-price-btn--dark">Получить FANKI EN</a>
                            </div>
                        </div>
                        {timeLeft > 0 && (
                            <div className="fk-timer fk-fi">
                                <p className="fk-timer-label">Цена действительна:</p>
                                <div className="fk-timer-blocks">
                                    <div className="fk-timer-block">
                                        <span className="fk-timer-num">{days}</span>
                                        <span className="fk-timer-unit">дн</span>
                                    </div>
                                    <span className="fk-timer-sep">:</span>
                                    <div className="fk-timer-block">
                                        <span className="fk-timer-num">{String(hours).padStart(2, '0')}</span>
                                        <span className="fk-timer-unit">час</span>
                                    </div>
                                    <span className="fk-timer-sep">:</span>
                                    <div className="fk-timer-block">
                                        <span className="fk-timer-num">{String(minutes).padStart(2, '0')}</span>
                                        <span className="fk-timer-unit">мин</span>
                                    </div>
                                    <span className="fk-timer-sep">:</span>
                                    <div className="fk-timer-block">
                                        <span className="fk-timer-num">{String(seconds).padStart(2, '0')}</span>
                                        <span className="fk-timer-unit">сек</span>
                                    </div>
                                </div>
                            </div>
                        )}
                        <p className="fk-pricing-note fk-fi">Разовая покупка на всю жизнь. Без подписки, без скрытых условий.</p>
                        <p className="fk-pricing-note fk-fi">Доступ к колоде будет в личном кабинете на сайте apexmeded.ru с 31 марта.</p>
                    </div>
                </section>

                {/* 7. OBJECTIONS */}
                <section className="fk-s fk-warm" id="fk-objections">
                    <div className="fk-w fk-w--narrow">
                        <h2 className="fk-fi">Частые вопросы</h2>

                        <div className="obj fk-fi">
                            <blockquote className="obj-q">«Я сам сделаю карточки.»</blockquote>
                            <p>Свои карточки - это топ, но только по question bank и слабым темам. Для создания карточек по всему First Aid при 3 минутах на карточку и 12&nbsp;000 карточках уйдёт больше 6 месяцев чистого времени. До начала повторения.</p>
                        </div>

                        <div className="obj fk-fi">
                            <blockquote className="obj-q">«В каком формате карточки?»</blockquote>
                            <p>.apkg - стандартный формат Anki. Открывается на любом устройстве: Windows, Mac, Linux, iOS, Android. Скачал, импортировал, начал.</p>
                        </div>

                        <div className="obj fk-fi">
                            <blockquote className="obj-q">«Подойдёт для Step 2 CK?»</blockquote>
                            <p>Основа та же - First Aid. Но FANKI заточен под Step&nbsp;1: базовые науки, патофизиология, фармакология. Для CK нужны клинические карточки - это отдельный продукт.</p>
                        </div>

                        <div className="obj fk-fi">
                            <blockquote className="obj-q">«Будут ли обновления?»</blockquote>
                            <p>Да. При изменениях в First Aid колода обновляется. Все обновления бесплатные - ты покупаешь один раз.</p>
                        </div>

                        <div className="obj fk-fi">
                            <blockquote className="obj-q">«Можно ли вернуть деньги?»</blockquote>
                            <p>Возврат не предусмотрен, но перед покупкой ты можешь скачать <a href="https://t.me/ashurovmed/3222" target="_blank" rel="noopener noreferrer" style={{color: 'var(--accent)', fontWeight: 600}}>бесплатный тестовый пак по всей иммунологии (440 карточек)</a> и оценить формат, качество и структуру.</p>
                        </div>
                    </div>
                </section>

                {/* 8. FINAL CTA */}
                <footer className="fk-s fk-deep final" id="fk-final">
                    <div className="fk-w final-w">
                        <p className="final-statement fk-fi">Весь First Aid. Одна колода. Одна покупка.</p>
                        <p className="final-anchor fk-fi">Пересдача Step&nbsp;1 стоит $700+. FANKI - инвестиция в первую попытку.</p>
                        <p className="final-price fk-fi">От 5&nbsp;900&nbsp;₽. Навсегда.</p>
                        <div className="final-btns fk-fi">
                            <a href="#fk-pricing" onClick={(e) => { e.preventDefault(); document.getElementById('fk-pricing')?.scrollIntoView({ behavior: 'smooth' }); }} className="fk-btn">
                                Получить FANKI
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                            </a>
                            <a href="https://t.me/ashurovmed/3222" target="_blank" rel="noopener noreferrer" className="fk-btn-ghost">Скачать 440 карточек бесплатно</a>
                        </div>
                        <div className="fk-anki-note fk-fi" style={{color: '#ffffff'}}>
                            <p style={{color: '#ffffff'}}><strong style={{color: '#ffffff'}}>Anki</strong> - бесплатная программа для создания и решения карточек (Windows, Mac, Linux, Android). На iOS - платная. <strong style={{color: '#ffffff'}}>FANKI</strong> - готовая колода с 12 500+ карточками для Anki.</p>
                        </div>

                        <div className="fk-footer-info fk-fi">
                            <div className="fk-footer-col">
                                <span className="fk-footer-brand">APEX</span>
                                <a href="https://apexmeded.ru/privacypolicy" target="_blank" rel="noopener noreferrer" className="fk-footer-link">Политика конфиденциальности</a>
                                <a href="https://apexmeded.ru/legal" target="_blank" rel="noopener noreferrer" className="fk-footer-link">Публичная оферта</a>
                            </div>
                            <div className="fk-footer-col">
                                <p>ИП Ашуров Мухаммаджон Фуркатович</p>
                                <p>ИНН 772996111132</p>
                                <p>ОГРНИП 325774600521362 от 12.08.2025</p>
                            </div>
                            <div className="fk-footer-col">
                                <p>ashurov.muhammad@yandex.ru</p>
                                <a href="https://t.me/md_ashurov" target="_blank" rel="noopener noreferrer" className="fk-footer-link">Телеграм: @md_ashurov</a>
                            </div>
                        </div>
                        <p className="fk-copy fk-fi">© 2026 FANKI. Все права защищены.</p>
                    </div>
                </footer>

            </main>
        </div>
    );
}
